import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import * as moment from 'moment';
import { forkJoin, map, Subject, takeUntil } from 'rxjs';

import { CalendarPhysicianFilterService } from 'src/app/core/services/calendar-physician-filter.service';
import { CalendarLocationFilterService } from 'src/app/core/services/calendar-location-filter.service';
import { GroupService } from 'src/app/core/services/group.service';
import { PatientAppointmentService } from 'src/app/core/services/patient-appointment.service';
import { ScheduleService } from 'src/app/core/services/schedule.service';
import { computeOpenSlots, OpenSlotCell, OpenSlotDay, OpenSlotItem, workingDays, workingDaysBefore } from 'src/app/core/utils/open-slots';

export interface OpenSlotsDialogData {
  startDate: Date;
}

/** A free slot to book, or the patient of a booking chosen in the grid. */
export type OpenSlotsDialogResult = { date: Date; timeSlot: string } | { patientId: string };

interface MonthRows {
  appointments: any[];
  events: any[];
}

/**
 * Open booking slots for the next working days as a grid (times down, days across). Each slot
 * lists what is in it (patient and appointment type, event title). Choosing a free slot closes the
 * dialog with its date and time for the calendar's booking flow; choosing a booking closes it with
 * that patient.
 */
@Component({
  selector: 'app-open-slots-dialog',
  templateUrl: './open-slots-dialog.component.html',
  styleUrls: ['./open-slots-dialog.component.scss']
})
export class OpenSlotsDialogComponent implements OnInit, OnDestroy {
  static readonly DAYS = 5;
  /** Entries shown in a slot before "+n". */
  static readonly SHOWN_ITEMS = 2;

  days: OpenSlotDay[] = [];
  times: string[] = [];
  loading = false;
  failed = false;
  /** Overridable in specs. */
  now = new Date();

  private schedule: any = null;
  private dates: Date[] = [];
  private readonly months = new Map<string, MonthRows>();
  private splitByPhysician = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<OpenSlotsDialogComponent, OpenSlotsDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: OpenSlotsDialogData,
    private patientAppointmentService: PatientAppointmentService,
    private scheduleService: ScheduleService,
    private calendarPhysicianFilter: CalendarPhysicianFilterService,
    private calendarLocationFilter: CalendarLocationFilterService,
    private groupService: GroupService,
    private cd: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.groupService.selectedGroup$
      .pipe(takeUntil(this.destroy$))
      .subscribe((g: any) => { this.splitByPhysician = !!g?.calendarSplitByPhysician; });

    this.scheduleService.schedule$
      .pipe(takeUntil(this.destroy$))
      .subscribe((schedule: any) => {
        if (!schedule) return;
        this.schedule = schedule;
        this.show(workingDays(this.dates[0] ?? this.data.startDate, OpenSlotsDialogComponent.DAYS, schedule.availableDays));
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get rangeLabel(): string {
    if (!this.dates.length) return '';
    const first = moment(this.dates[0]);
    const last = moment(this.dates[this.dates.length - 1]);
    return `${first.format('D MMM')} – ${last.format('D MMM YYYY')}`;
  }

  next(): void {
    const last = this.dates[this.dates.length - 1];
    if (!last) return;
    const from = moment(last).add(1, 'day').toDate();
    this.show(workingDays(from, OpenSlotsDialogComponent.DAYS, this.schedule?.availableDays));
  }

  previous(): void {
    if (!this.dates[0]) return;
    this.show(workingDaysBefore(this.dates[0], OpenSlotsDialogComponent.DAYS, this.schedule?.availableDays));
  }

  choose(day: OpenSlotDay, cell: OpenSlotCell): void {
    if (cell.state !== 'free') return;
    this.dialogRef.close({ date: day.date, timeSlot: cell.time });
  }

  /** "Free" only for an empty slot; a slot with something in it says how many places are left. */
  label(cell: OpenSlotCell): string {
    if (cell.busy) return 'Busy';
    if (cell.state === 'full') return 'Full';
    return cell.taken > 0 && cell.remaining !== null ? `${cell.remaining} left` : 'Free';
  }

  visibleItems(cell: OpenSlotCell): OpenSlotItem[] {
    return cell.items.slice(0, OpenSlotsDialogComponent.SHOWN_ITEMS);
  }

  hiddenCount(cell: OpenSlotCell): number {
    return Math.max(cell.items.length - OpenSlotsDialogComponent.SHOWN_ITEMS, 0);
  }

  /** Everything in the slot, one per line. */
  cellTooltip(cell: OpenSlotCell): string {
    return cell.items.map(i => [i.title, i.detail].filter(Boolean).join(' · ')).join('\n');
  }

  openPatient(item: OpenSlotItem, event: MouseEvent): void {
    event.stopPropagation();
    if (item.patientId) this.dialogRef.close({ patientId: item.patientId });
  }

  trackByKey(_: number, day: OpenSlotDay): string {
    return day.dateKey;
  }

  private show(dates: Date[]): void {
    if (!dates.length) return;
    this.dates = dates;

    const missing = Array.from(new Set(dates.map(d => moment(d).format('YYYY-MM'))))
      .filter(key => !this.months.has(key));
    if (!missing.length) {
      this.refresh();
      return;
    }

    this.loading = true;
    this.failed = false;
    const options = { skipSharedMonthCache: true };
    forkJoin(missing.map(key => {
      const [year, month] = key.split('-').map(Number);
      return forkJoin([
        this.patientAppointmentService.loadAppointmentsByMonth(year, month, options),
        this.scheduleService.loadEventsByMonth(year, month, options),
      ]).pipe(map(([appointments, events]) => ({ key, appointments: rows(appointments), events: rows(events) })));
    }))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: loaded => {
          loaded.forEach(m => this.months.set(m.key, { appointments: m.appointments, events: m.events }));
          this.loading = false;
          this.refresh();
        },
        error: () => {
          this.loading = false;
          this.failed = true;
          this.cd.markForCheck();
        }
      });
  }

  private refresh(): void {
    const appointments: any[] = [];
    const events: any[] = [];
    this.months.forEach(m => { appointments.push(...m.appointments); events.push(...m.events); });

    const physicianId = this.calendarPhysicianFilter.getSelectedPhysicianId();
    const visibleAppointments = this.calendarLocationFilter.applyAppointmentFilter(
      this.calendarPhysicianFilter.applyAppointmentFilter(appointments, this.splitByPhysician, physicianId),
    ) ?? [];
    // An event for another physician does not hold this physician's slots.
    const visibleEvents = this.calendarPhysicianFilter.applyAppointmentFilter(events, this.splitByPhysician, physicianId) ?? [];

    this.days = computeOpenSlots(this.dates, [...visibleAppointments, ...visibleEvents], this.schedule, this.now);
    this.times = this.days[0]?.cells.map(c => c.time) ?? [];
    this.cd.markForCheck();
  }
}

/** The month endpoints answer `{ data: [...] }` or, from some paths, the array itself. */
function rows(response: any): any[] {
  if (Array.isArray(response)) return response;
  return Array.isArray(response?.data) ? response.data : [];
}
